import { useCallback, useEffect, useRef, useState } from 'react';
import { rms, WAVEFORM_BARS } from './audioLevels';

interface Options {
  // Given the finished recording as a file.
  onRecorded: (file: File) => void;
}

// How often the microphone's level is sampled for the live waveform.
const LEVEL_INTERVAL_MS = 150;

// Records from the microphone: the elapsed time and a live waveform while
// it runs, and the clip as a file when it stops.
export function useAudioRecorder({ onRecorded }: Options) {
  const [isRecording, setIsRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [levels, setLevels] = useState<number[]>([]);
  const [isDenied, setIsDenied] = useState(false);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const audioContextRef = useRef<AudioContext | null>(null);
  const timersRef = useRef<ReturnType<typeof setInterval>[]>([]);
  const onRecordedRef = useRef(onRecorded);
  onRecordedRef.current = onRecorded;

  const cleanUp = useCallback(() => {
    timersRef.current.forEach(clearInterval);
    timersRef.current = [];
    recorderRef.current?.stream.getTracks().forEach((track) => track.stop());
    recorderRef.current = null;
    audioContextRef.current?.close().catch(() => {});
    audioContextRef.current = null;
  }, []);

  // Stop any in-flight recording if the form unmounts.
  useEffect(() => cleanUp, [cleanUp]);

  const start = async () => {
    setIsDenied(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      recorder.onstop = () => {
        const type = recorder.mimeType || 'audio/webm';
        const ext = type.includes('mp4') ? 'm4a' : 'webm';
        const blob = new Blob(chunksRef.current, { type });
        onRecordedRef.current(
          new File([blob], `recording-${Date.now()}.${ext}`, { type }),
        );
      };

      // The live waveform: the microphone's loudness, sampled a few times
      // a second, newest on the right.
      if (typeof AudioContext !== 'undefined') {
        const context = new AudioContext();
        const analyser = context.createAnalyser();
        analyser.fftSize = 1024;
        context.createMediaStreamSource(stream).connect(analyser);
        audioContextRef.current = context;
        const samples = new Float32Array(analyser.fftSize);
        timersRef.current.push(
          setInterval(() => {
            analyser.getFloatTimeDomainData(samples);
            // Speech peaks around 0.3 RMS; scale so it fills the bars.
            const level = Math.min(1, rms(samples) * 3);
            setLevels((previous) => [...previous, level].slice(-WAVEFORM_BARS));
          }, LEVEL_INTERVAL_MS),
        );
      }

      recorder.start();
      recorderRef.current = recorder;
      setElapsed(0);
      setLevels([]);
      setIsRecording(true);
      timersRef.current.push(
        setInterval(() => setElapsed((seconds) => seconds + 1), 1000),
      );
    } catch {
      setIsDenied(true);
    }
  };

  const stop = () => {
    recorderRef.current?.stop();
    cleanUp();
    setIsRecording(false);
  };

  return { isRecording, elapsed, levels, isDenied, start, stop };
}
